
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DateTime } from 'luxon';

import { sharedState } from '../../../framework/state';
import { getDatabaseOptions, OptionValue } from '../../../framework/service/options';
import { dateToDbMonthDateString } from '../../../../common/framework/util/convert';

import { Customer } from '../../../../common/application/model/Customer';
import { CustomerReport } from '../../../../common/application/model/report/CustomerReport';
import { CustomerReportVehicleTypeRow } from '../../../../common/application/model/report/CustomerReportVehicleTypeRow';
import { CustomerMakesReport } from '../../../../common/application/model/report/CustomerMakesReport';
import { CostReport } from '../../../../common/application/model/report/CostReport';

import BudgetSpent from '../manager/report/BudgetSpent.vue';
import ViewCard from '../manager/home/ViewCard.vue';
import AutoCompleteMultipleField from '../../fields/AutoCompleteMultipleField.vue';
import VehicleStates from '../manager/report/VehicleStates.vue';

import {
    getCommonGroupName,
    getMultipleCustomerFleetMakeReport,
    getMultipleCustomerFleetReport,
} from '../../client/report_client';
import { mapCostReportRows, setVehicleTypeRows } from '../../service/report_service';
import { getAccessibleCustomers } from '../../service/resource_service';
import ParameterService from '../../service/parameter_service';
import { ApplicationResource } from '../../../../common/application/enumeration/ApplicationResource';
import { CustomerReportCostRow } from '../../../../common/application/model/report/CustomerReportCostRow';

@Component({
    components: {
        VehicleStates,
        BudgetSpent,
        ViewCard,
        AutoCompleteMultipleField,
    },
})
export default class Dashboard extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_COST_MONTHLY;
    parameterService = new ParameterService(this.resourceType);

    // State
    shared = sharedState;
    local = {
        customers: [] as Customer[],
        customerFleetReport: {
            vehicleTypeRows: [] as CustomerReportVehicleTypeRow[],
            costRows: [] as CustomerReportCostRow[],
            co2average: 0,
            customerId: '',
            customerName: '',
        } as CustomerReport,
        vehicleMakesRows: [] as CustomerMakesReport[],
        vehicleTypeRows: [] as CustomerReportVehicleTypeRow[],
        showWithVat: false,
        invoiceRowTypeOptions: [] as OptionValue[],
        costReport: [] as CostReport[],
        selectedMonth: undefined as string | undefined,
        loading: false,
        title: '',
        parameters: this.parameterService.get(),
    };

    // Functions
    async created() {
        if (this.$route.params && this.$route.params.customerId) {
            this.local.parameters.customerId = [this.$route.params.customerId];
            this.parameterService.set(this.local.parameters);
        }

        this.local.title = this.$t('title.dashboard').toString();
        this.local.loading = true;

        if (new Date().getMonth() === 0) {
            this.local.selectedMonth = dateToDbMonthDateString(DateTime.now().toJSDate());
        } else if (new Date().getDate() >= 15) {
            this.local.selectedMonth = dateToDbMonthDateString(DateTime.now().minus({ months: 1 }).toJSDate());
        } else {
            this.local.selectedMonth = dateToDbMonthDateString(DateTime.now().minus({ months: 2 }).toJSDate());
        }
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);

        this.local.customers = await getAccessibleCustomers();

        if (!this.local.parameters.customerId || !this.local.parameters.customerId.length) {
            this.local.parameters.customerId = this.local.customers.map(({ id }) => id);
            this.parameterService.set(this.local.parameters);
        }

        await this.loadAsyncData();

        this.local.loading = false;
    }

    async dropdownClosed(resourceIds: string[], idField: string): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.selectedMonth')
    async selectedMonthChange() {
        if (this.local.loading) {
            return;
        }

        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    get monthsIncluded(): string {
        if (!this.local.selectedMonth) {
            return '-';
        }

        return `${DateTime.now().startOf('year').toFormat('MM/yyyy')} - ${DateTime.fromFormat(
            this.local.selectedMonth,
            'yyyy-MM',
        ).toFormat('MM/yyyy')}`;
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    private async loadAsyncData() {
        if (this.local.parameters.customerId && this.local.parameters.customerId.length) {
            this.local.customerFleetReport = await getMultipleCustomerFleetReport(this.local.parameters.customerId);
            this.local.vehicleMakesRows = await getMultipleCustomerFleetMakeReport(this.local.parameters.customerId);
        }

        this.local.vehicleTypeRows = setVehicleTypeRows(this.local.customerFleetReport, this);

        let currentMonth = dateToDbMonthDateString(new Date());

        if (this.local.selectedMonth) {
            currentMonth = this.local.selectedMonth;
        }

        this.setCostReport(currentMonth);

        await this.getDashboardTitle();
    }

    private setCostReport(monthAsString: string) {
        this.local.costReport = mapCostReportRows(
            this.local.customerFleetReport.costRows,
            [monthAsString],
            this.local.invoiceRowTypeOptions,
        );
    }

    private async getDashboardTitle(): Promise<void> {
        const defaultTitle = this.$t('title.dashboard').toString();
        if (this.local.parameters.customerId && this.local.parameters.customerId.length === 1) {
            const selectedCustomer = this.local.customers.find(({ id }) => this.local.parameters.customerId[0] === id);

            if (!selectedCustomer) {
                this.local.title = defaultTitle;
                return;
            }

            this.local.title = selectedCustomer.name;
            return;
        } else if (this.local.parameters.customerId && this.local.parameters.customerId.length > 1) {
            const result = await getCommonGroupName(this.local.parameters.customerId);

            if (result.groupName) {
                this.local.title = result.groupName;
                return;
            }
        }

        this.local.title = defaultTitle;
    }
}
